import { useContext, useEffect } from "react"

import { Card, CardContent } from "@components/common/card"
import { ALL_MAIN_BOQ_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import axios from "axios"
import { useAuth } from "@pma/stores/auth-store"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import { DictType } from "@pma/types"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import useTranslator from "@pma/hooks/use-translator"
import PmsLoader from "@components/common/loader"
import { BillDetails } from "./bill-details"
import CreateMainBoQ from "./create-main-boq"
import CreateNewBoQEstimate from "./create-new-boq-estimate"
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

export function MainBoqsTable() {
  const { translate, locale } = useTranslator()

  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const navigate = useNavigate()
  const projectId = useParams().projectId
  const { userRoleDetails } = useContext(UserRoleContext)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id

  const activeBoQ = searchParam.get("main")

  const estimateBoqId = authConfig?.financial?.estimate

  const allStateTabs = authConfig?.tabs ?? {}
  const mainBoQActiveTab = allStateTabs ? allStateTabs["boq"] : undefined
  const activeTabs: DictType = mainBoQActiveTab || {
    main: "main",
    sub: "boq-tender",
    subChild: "bills",
  }

  const getMainBoQs = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ALL_MAIN_BOQ_ENDPOINT,
      {
        organization_id: orgId,
        project_id: projectId,
        show_deleted: false
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,

          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data: mainBoQs,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["main-boq", orgId, projectId],
    queryFn: getMainBoQs,
  })

  const setActiveBoQId = (boqId: string) => {
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.delete("estimate")
    currentParams.delete("estimates")
    currentParams.set("main", boqId)

    if (projectId && boqId) {
      try { sessionStorage.setItem(`pma_last_boq_${projectId}`, boqId) } catch {}
    }

    !!boqId && navigate(`?${currentParams.toString()}`)
  }

  const filteredMainBoqs = mainBoQs

  useEffect(() => {
    if (!isLoading && !activeBoQ && activeTabs?.main === "main" && mainBoQs?.length > 0) {
      // Try to restore last selected BoQ for this project
      let targetId = ""
      if (projectId) {
        try { targetId = sessionStorage.getItem(`pma_last_boq_${projectId}`) || "" } catch {}
      }

      // Verify the saved BoQ still exists in the list
      if (!targetId || !mainBoQs.find((b: DictType) => String(b.id) === String(targetId))) {
        const sorted = [...mainBoQs].sort((a: DictType, b: DictType) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
        targetId = sorted[0]?.id || ""
      }

      if (targetId) setActiveBoQId(targetId)
    }
  }, [activeTabs?.main, activeBoQ, isLoading, filteredMainBoqs])

  // Always sync current selection to sessionStorage (covers URL param from refresh, dropdown changes, etc.)
  useEffect(() => {
    if (projectId && activeBoQ) {
      try { sessionStorage.setItem(`pma_last_boq_${projectId}`, activeBoQ) } catch {}
    }
  }, [projectId, activeBoQ])

  return (
    <>
      <Card>
        <CardContent className='p-2'>
          {isLoading ? (
            <PmsLoader />
          ) : (
            <>
              {!estimateBoqId ? (
                <div className='flex w-full flex-col items-center justify-center py-4 text-sm font-medium'>
                  <p>{translate("No estimates found")}</p>
                  <br />
                  {userRoleDetails?.designer || userRoleDetails?.supervisor || userRoleDetails.fin_manager ? (
                    <CreateNewBoQEstimate revalidate={refetch} projectId={projectId} />
                  ) : null}
                </div>
              ) : filteredMainBoqs?.length < 1 ? (
                <div className='flex w-full flex-col items-center justify-center py-4 text-sm font-medium'>
                  <p>{translate("No BoQs found")}</p>
                  <br />

                  {(userRoleDetails?.supervisor || userRoleDetails?.fin_manager) ? (
                    <CreateMainBoQ
                      revalidate={refetch}
                      projectId={projectId}
                      estimateBoQId={estimateBoqId}
                    />
                  ) : null}
                </div>
              ) : null}
            </>
          )}

          {activeBoQ && !isLoading && filteredMainBoqs?.length > 0 && (
            <BillDetails boqId={activeBoQ} projectId={projectId!} />
          )}
        </CardContent>
      </Card>
    </>
  )
}
